export type ConsentStatus = 'granted' | 'declined' | 'revoked';

export type ConsentType =
  | 'terms'
  | 'privacy'
  | 'health_data_processing'
  | 'genetics_context'
  /**
   * Consent §4.1(4) — "the separate, explicit retention of the person's raw
   * genetic data for future re-analysis".
   *
   * SEPARATE from genetics_context, which is about the nature of the test
   * ("a context-only SNP panel, not a diagnostic service"). §4.1 lists retention
   * as its own thing, and §11.3 requires it to be withdrawable on its own: "A
   * Member may withdraw consent to Melio's retention of their raw genetic data
   * separately from the rest of their account, and that data is then deleted
   * without the membership ending."
   *
   * The Privacy Notice already promises the member exactly this. Until now
   * nothing asked for it and nothing could act on withdrawing it.
   */
  | 'genetic_data_retention'
  /**
   * Recording the intake interview, and keeping the recording.
   *
   * Modelled on `genetic_data_retention` deliberately, because it is the same
   * shape: consent to RETAIN an artefact, held separately from consent to the
   * thing that produced it (`consultation_care`), withdrawable on its own, and
   * whose withdrawal destroys the artefact while leaving the clinical record
   * standing.
   *
   * That last part is the design. The clinician's interview notes ARE the
   * record — RecordingBlock says so, "the transcript is a check on it, not a
   * substitute for it" — so withdrawing this deletes the recording, the
   * transcript and any notes generated from them, and changes nothing about
   * what was written down.
   *
   * Until now this was a bare boolean on the interview document, outside
   * consent_events entirely: the member's consent history told two stories, and
   * §14.1's three requirements (the date, the version agreed, the identity of
   * the clinician who took it) had no home for it. Scope and outstanding
   * decisions: docs/clinical/RECORDING_CONSENT_AND_RETENTION.md.
   */
  | 'interview_recording'
  /**
   * RETIRED — Consent §4.4: "Melio does not seek consent for any purpose beyond
   * those listed in Section 4.1." Neither research use nor commercial data use is
   * among §4.1's six purposes, so both are no longer asked for.
   *
   * The union members stay so historical events still parse. Members granted or
   * declined these, and a consent record that cannot be read is worse than one
   * that should not have been collected. Nothing writes them; see
   * RETIRED_CONSENT_TYPES.
   */
  | 'research_use'
  | 'commercial_data_use'
  | 'concierge_act_on_behalf'
  | 'concierge_share_with_provider'
  | 'concierge_insurance_access'
  /**
   * Ask Melio — acknowledgement that the feedback chat is an AI system, that
   * conversations are reviewed but not monitored in real time, and that message
   * text is processed by a third-party AI provider.
   *
   * NOTE: the chat records its own acknowledgement on users/{uid} keyed to
   * SAFETY_COPY_VERSION, because the copy version and the consent version move
   * independently. This union member exists so the same acknowledgement can be
   * written through recordConsentEvents once the DPO confirms it belongs in the
   * consent_events audit trail — see the open decisions in the plan.
   */
  | 'feedback_chat_ai'
  /**
   * Consent §4.2 — confirmed again inside each consultation, before any material
   * step, because "a tick recorded at registration does not discharge the duty to
   * check that the person still agrees and still understands".
   *
   * Distinct from the registration pack rather than a re-write of it: the event
   * records that the member reconfirmed, and `version` carries the pack version
   * they originally agreed (resolved server-side from their own consent record,
   * never supplied by the client — a client-supplied version is a client-supplied
   * audit trail). Together with capturedAt and capturedByUid that satisfies the
   * three things §14.1 requires: the date, the version agreed, and the identity of
   * the clinician who confirmed it.
   *
   * Written only by recordConsultationAttestation. The member app never writes it.
   */
  | 'consultation_care';

export type ConsentSourceSurface =
  | 'book_page'
  | 'booking_flow'
  | 'booking_flow_concierge'
  | 'ops_portal'
  | 'doctor_portal'
  | 'system'
  /** Blocking gate on first entry to the member app (FirstLoginConsentGate). */
  | 'member_app_first_login'
  /** The blocking notice on /feedback, before a first message can be sent. */
  | 'feedback_chat'
  /** Where a member changes or withdraws a consent afterwards (Consent §3.4). */
  | 'member_app_consent_settings';

/**
 * Consents a member MUST grant before using the app. Terms and privacy are the
 * agreement itself; health_data_processing is the explicit condition for
 * handling special-category health and genetic data.
 */
export const REQUIRED_CONSENT_TYPES: ConsentType[] = [
  'terms',
  'privacy',
  'health_data_processing',
  /**
   * Moved here from OPTIONAL_CONSENT_TYPES, where it contradicted the product.
   *
   * The list below said optional consents "must never block access, or they would
   * not be freely given" — while /enrollment and /book both gated the pay button
   * on `consents.geneticsContext` and rendered it under a heading reading
   * "Required". Consent §10.2 permits a mandatory element: "Each confirmation in
   * the Consent Pack is required for the Service as it is currently offered. Where
   * a person declines one, Melio cannot provide the Service." So the UI was right
   * and the classification was wrong.
   *
   * NOT the same as consenting to RETAIN the raw data — that is
   * genetic_data_retention, which is genuinely separable and genuinely optional.
   */
  'genetics_context',
];

/**
 * Consents that are genuinely optional — refusing any of them must never block
 * access, or they would not be freely given.
 *
 * These are NOT asked by the first-login gate, which covers required consents
 * only. They are captured at /enrollment (buildBookPageConsentEvents), so a
 * member provisioned directly — e.g. by the cohort seeder, which bypasses
 * enrolment — has no record for them either way. Anything that depends on one
 * of these should check for an explicit 'granted' rather than assume.
 */
export const OPTIONAL_CONSENT_TYPES: ConsentType[] = [
  /**
   * The only genuinely optional one, and the only one that fits the rule above:
   * a member who declines retention still gets the Service — Melio interprets
   * their panel and then does not keep the raw genotypes for re-analysis.
   */
  'genetic_data_retention',
];

/**
 * Consents Melio must stop seeking — Consent §4.4.
 *
 * "Melio does not seek consent for any purpose beyond those listed in Section
 * 4.1. A purpose not covered by the current Consent Pack is not pursued, and data
 * is not collected for it." §4.1 lists six purposes; research use and commercial
 * data use are not among them.
 *
 * Kept as a named list rather than deleted so historical events still parse and
 * the audit trail stays readable. Nothing writes these. Mirrors
 * RETIRED_CONSENT_TYPES in functions/lib/consentPolicy.js.
 *
 * If Melio decides it does want either purpose, the route is Consent Pack v1.3
 * and a §4.1 amendment — not re-adding a checkbox.
 */
export const RETIRED_CONSENT_TYPES: ConsentType[] = [
  'research_use',
  'commercial_data_use',
];

export interface ConsentArtifactRef {
  collection?: string | null;
  documentId?: string | null;
  path?: string | null;
  note?: string | null;
}

export interface ConsentEventInput {
  consentType: ConsentType;
  version: string;
  status: ConsentStatus;
  sourceSurface: ConsentSourceSurface;
  artifactRef?: ConsentArtifactRef | null;
}

export interface ConsentEventRecord extends ConsentEventInput {
  id: string;
  userId: string;
  capturedAt?: unknown;
  capturedByUid?: string | null;
  capturedByEmail?: string | null;
  capturedByRole?: string | null;
  sourceSystem?: string | null;
  schemaVersion?: number;
  modelVersion?: string | null;
}

export interface ConsentSummaryRecord {
  consentType: ConsentType;
  latestEventId?: string;
  latestStatus: ConsentStatus;
  latestVersion: string;
  sourceSurface?: ConsentSourceSurface;
  artifactRef?: ConsentArtifactRef | null;
  capturedAt?: unknown;
  capturedByUid?: string | null;
  capturedByEmail?: string | null;
  capturedByRole?: string | null;
  latestGrantedAt?: unknown;
  createdAt?: unknown;
  updatedAt?: unknown;
  schemaVersion?: number;
  modelVersion?: string | null;
}

export interface ConsentCollectionSnapshot {
  terms: boolean;
  privacy: boolean;
  healthDataProcessing: boolean;
  geneticsContext: boolean;
  /** §4.1(4) — separate from geneticsContext, and genuinely optional. */
  geneticDataRetention: boolean;
}

export const CONSENT_MODEL_VERSION = '2026-03-consent-v1';

/**
 * Stamped on every onboarding consent event, and the only record of WHICH
 * document text a member agreed to. Bump it whenever the patient-facing terms
 * or privacy text changes materially, so old records keep pointing at the text
 * that was actually shown.
 *
 * v2 (this value) corresponds to Privacy Notice v1.2 (22 June 2026), ported
 * verbatim to /privacy — see config/legalDocuments. The previous value,
 * '2026-03-onboarding-v1', predated both v1.1 and v1.2 of that notice and was
 * still being written months after the notice had moved on.
 *
 * NOTE: bumping this does not re-prompt anyone. There is no re-consent gate
 * yet, so members who accepted v1 are not asked again; their records correctly
 * show the older version.
 */
export const ONBOARDING_CONSENT_VERSION = '2026-06-onboarding-v2';
export const CONCIERGE_CONSENT_VERSION = '2026-03-concierge-v1';
